import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import { useMemo, useState } from 'react';

import type { ApiErrorDataI } from 'declarations/interfaces/errors';
import { extractErrorExtensionField } from 'helpers/errors/extractErrorExtensionField';

const getDialogPaperBackground = (theme: Theme): string =>
  theme.palette.background.paperElevation0 ??
  theme.palette.background.paper ??
  theme.palette.background.default;

const getCodeBlockBackground = (theme: Theme): string =>
  theme.palette.background.paperElevation2 ??
  theme.palette.background.paperElevation1 ??
  theme.palette.action.hover;

const getOutlinedBorder = (theme: Theme): string =>
  `1px solid ${theme.palette.border?.outlined ?? theme.palette.divider}`;

const dialogPaperSx: SxProps<Theme> = {
  backgroundColor: getDialogPaperBackground,
  color: 'text.primary',
  borderRadius: '8px',
  boxShadow: (theme: Theme): string => theme.shadows[6],
  border: getOutlinedBorder,
};

const codeBlockSx: SxProps<Theme> = {
  backgroundColor: getCodeBlockBackground,
  borderRadius: 1,
  border: getOutlinedBorder,
  color: 'inherit',
};

interface ErrorDetailsModalProps {
  errorDetails: ApiErrorDataI;
  errorSource?: string;
  onClose: () => void;
  open: boolean;
}

const ErrorDetailsModal: FC<ErrorDetailsModalProps> = ({
  errorDetails,
  errorSource,
  onClose,
  open,
}) => {
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>('idle');

  const prettyJson = useMemo(() => JSON.stringify(errorDetails, null, 2), [errorDetails]);

  const traceId = extractErrorExtensionField(errorDetails, 'traceId');
  const developerMessage = extractErrorExtensionField(errorDetails, 'developerMessage');
  const errorType = extractErrorExtensionField(errorDetails, 'errorType');
  const stackTrace = extractErrorExtensionField(errorDetails, 'stackTrace');

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(prettyJson);
      setCopyStatus('copied');
      window.setTimeout(() => setCopyStatus('idle'), 1500);
    } catch {
      setCopyStatus('failed');
      window.setTimeout(() => setCopyStatus('idle'), 1500);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: (theme: Theme): string =>
              theme.palette.background.backdrop ?? 'rgba(0, 0, 0, 0.5)',
          },
        },
      }}
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: 'text.primary',
        }}
      >
        Error details
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ color: 'text.primary', borderColor: 'divider' }}>
        <Box display="flex" flexDirection="column" gap={3}>
          <Box
            alignItems="center"
            display="flex"
            justifyContent="space-between"
            gap={2}
            width="100%"
          >
            <Box display="flex" flexDirection="column" gap={0.5}>
              {errorSource && (
                <Typography
                  sx={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
                  variant="body2"
                >
                  Source: {errorSource}
                </Typography>
              )}
              {traceId && (
                <Typography
                  sx={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
                  variant="body2"
                >
                  Trace ID: {traceId}
                </Typography>
              )}
              {Number.isFinite(errorDetails.status) && errorDetails.status > 0 && (
                <Typography
                  sx={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
                  variant="body2"
                >
                  Status: {errorDetails.status}
                </Typography>
              )}
              {errorDetails.title && (
                <Typography
                  sx={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
                  variant="body2"
                >
                  Title: {errorDetails.title}
                </Typography>
              )}
              {errorDetails.detail && (
                <Typography
                  sx={{
                    overflowWrap: 'break-word',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                  variant="body2"
                >
                  Detail: {errorDetails.detail}
                </Typography>
              )}
              {errorType && (
                <Typography
                  sx={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
                  variant="body2"
                >
                  Error type: {errorType}
                </Typography>
              )}
            </Box>
            <Button onClick={handleCopy} size="small" variant="outlined">
              {((): string => {
                if (copyStatus === 'copied') return 'Copied';
                if (copyStatus === 'failed') return 'Copy failed';
                return 'Copy JSON';
              })()}
            </Button>
          </Box>

          {errorDetails.errors && Object.keys(errorDetails.errors).length > 0 && (
            <>
              <Divider />
              <Box width="100%">
                <Typography gutterBottom variant="subtitle2">
                  Validation errors
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {Object.entries(errorDetails.errors).map(([field, messages]) => (
                    <li key={field}>
                      <Typography
                        sx={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
                        variant="body2"
                      >
                        <strong>{field}:</strong> {messages.join(', ')}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            </>
          )}

          {(developerMessage || stackTrace) && (
            <>
              <Divider />
              {developerMessage && (
                <Box width="100%">
                  <Typography gutterBottom variant="subtitle2">
                    Developer message
                  </Typography>
                  <Box
                    sx={{
                      ...codeBlockSx,
                      overflowWrap: 'break-word',
                      p: 2,
                      whiteSpace: 'pre-wrap',
                      width: '100%',
                      wordBreak: 'break-word',
                    }}
                  >
                    <Typography sx={{ fontFamily: 'inherit', fontSize: 'inherit' }} variant="body2">
                      {developerMessage}
                    </Typography>
                  </Box>
                </Box>
              )}
              {stackTrace && (
                <Box width="100%">
                  <Typography gutterBottom variant="subtitle2">
                    Stack trace
                  </Typography>
                  <Box
                    sx={{
                      ...codeBlockSx,
                      fontFamily: 'Consolas, Menlo, Monaco, "Courier New", monospace',
                      fontSize: 12,
                      overflowWrap: 'break-word',
                      p: 2,
                      whiteSpace: 'pre-wrap',
                      width: '100%',
                      wordBreak: 'break-word',
                    }}
                  >
                    {stackTrace}
                  </Box>
                </Box>
              )}
            </>
          )}

          <Divider />

          <Box width="100%">
            <Typography gutterBottom variant="subtitle2">
              Raw payload
            </Typography>
            <Box
              sx={{
                ...codeBlockSx,
                fontFamily: 'Consolas, Menlo, Monaco, "Courier New", monospace',
                fontSize: 12,
                overflowWrap: 'break-word',
                p: 2,
                whiteSpace: 'pre-wrap',
                width: '100%',
              }}
            >
              {prettyJson}
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ErrorDetailsModal;
